import { emptySplitApi } from "services/empty-split-api";
import {
  BulkStatusRequest,
  BulkStatusResponse,
  FollowUp,
  FollowUpCounts,
  FollowUpGenerationDetail,
  FollowUpGenerationTaskStatus,
  FollowUpGenerationsResponse,
  FollowUpKinds,
  FollowUpPromptVersionsResponse,
  FollowUpRecordingsFilters,
  FollowUpRecordingsResponse,
  FollowUpScopeFilters,
  FollowUpStatus,
  SuggestFollowUpsResponse,
  TranscriptLinesResponse
} from "services/types/follow-up.type";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

/** Matches the backend's `default_limit` on the recordings list. `max_limit` is 50. */
export const FOLLOW_UPS_PAGE_SIZE = 10;

/** `max_limit` on the transcript-lines endpoint, and the prompt's own line cap. */
export const TRANSCRIPT_LINES_MAX_LIMIT = 2000;

/**
 * `formatValidQueryParamUrlCommaSeparated` interpolates raw — no escaping — so
 * an unencoded `&` in a search term truncates the query string and silently
 * drops every filter after it. `services/collections.ts` encodes for the same
 * reason. Applied at the single boundary rather than at each call site.
 *
 * Exported for the URL-building tests: the failure mode is a well-formed URL
 * that quietly means something else, which no runtime assertion would catch.
 */
export function withEncodedSearch<T extends { search?: string }>(
  filters: T
): T {
  return filters.search
    ? { ...filters, search: encodeURIComponent(filters.search) }
    : filters;
}

/**
 * Named rather than an inline `Filters & {offset; limit}` intersection.
 *
 * This repo already sits near TypeScript's relation-cache ceiling — `yarn
 * type-check` can die with `RangeError: Map maximum size exceeded`, and
 * `tsconfig.memory-check.json` exists to exclude `src/services/**` for exactly
 * that reason. Inline intersections in generic argument positions are the
 * expensive shape (`getCrossProductIntersections` is in that stack trace), so
 * every query arg here gets its own named interface and is referenced once.
 */
export interface FollowUpRecordingsQueryArgs extends FollowUpRecordingsFilters {
  offset?: number;
  limit?: number;
}

export interface FollowUpGenerationsQueryArgs {
  ambient_scribe: number;
  limit?: number;
  offset?: number;
}

export interface FollowUpKindsQueryArgs {
  patient?: number;
  clinic?: number;
  limit?: number;
  offset?: number;
}

export interface TranscriptLinesQueryArgs {
  ambientScribeId: number;
  limit?: number;
  offset?: number;
}

export interface FollowUpStatusUpdateArgs {
  id: number;
  status: FollowUpStatus;
}

/** The recordings-list URL. Exported so the query-string rules are testable. */
export function buildRecordingsUrl({
  offset = 0,
  limit = FOLLOW_UPS_PAGE_SIZE,
  ...filters
}: FollowUpRecordingsQueryArgs): string {
  const filtersString = formatValidQueryParamUrlCommaSeparated(
    withEncodedSearch(filters)
  );
  return `/ai/follow-up-recordings/?limit=${limit}&offset=${offset}&${filtersString}`;
}

export const followUpsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    /**
     * The page. A PLAIN query cached by FULL args (offset included), so each
     * page replaces the last — deliberately not `createPaginatedEndpoint`.
     *
     * That helper collapses every page into one cache entry and reads with only
     * the current offset, so a `pollingInterval` or a tag invalidation would
     * refresh just the last page loaded. Generation progress is polled through
     * THIS endpoint, so a recording finishing on an earlier page would never
     * update and its spinner would never stop. Same trade-off, same resolution
     * as `getScribeInsightsPage`.
     */
    getFollowUpRecordings: builder.query<
      FollowUpRecordingsResponse,
      FollowUpRecordingsQueryArgs
    >({
      query: buildRecordingsUrl,
      providesTags: ["FollowUpRecordings"]
    }),

    /**
     * Facet counts plus the patient list for the dropdown. Takes only SCOPE
     * filters — the backend ignores `status` and `type` here on purpose, so the
     * counts answer "where could I go?" rather than echoing the current view.
     */
    getFollowUpCounts: builder.query<FollowUpCounts, FollowUpScopeFilters>({
      query: (filters) =>
        `/ai/follow-up-recordings/counts/${formatValidQueryParamUrlCommaSeparated(
          withEncodedSearch(filters),
          true
        )}`,
      providesTags: ["FollowUpRecordings"]
    }),

    /**
     * Unused by the list view — the modal renders from the row already in hand.
     * Kept for a future URL-addressable modal. Note there is no flat list route
     * at `/ai/follow-ups/`; it 404s.
     */
    getFollowUp: builder.query<FollowUp, number>({
      query: (id) => `/ai/follow-ups/${id}/`,
      providesTags: ["FollowUps"]
    }),

    /**
     * Only `status` is writable; any other key is rejected per-field. Returns
     * the full updated object so an open modal re-renders without a second read.
     */
    updateFollowUpStatus: builder.mutation<FollowUp, FollowUpStatusUpdateArgs>({
      query: ({ id, status }) => ({
        url: `/ai/follow-ups/${id}/`,
        method: "PATCH",
        body: { status }
      }),
      invalidatesTags: ["FollowUps", "FollowUpRecordings"]
    }),

    bulkUpdateFollowUpStatus: builder.mutation<
      BulkStatusResponse,
      BulkStatusRequest
    >({
      query: (body) => ({
        url: "/ai/follow-ups/bulk-status/",
        method: "PATCH",
        body
      }),
      invalidatesTags: ["FollowUps", "FollowUpRecordings"]
    }),

    /** Honours patient/clinic/date scope. `search` is accepted but IGNORED. */
    getFollowUpKinds: builder.query<FollowUpKinds, FollowUpKindsQueryArgs>({
      query: (params) =>
        `/ai/follow-ups/kinds/${formatValidQueryParamUrlCommaSeparated(
          params,
          true
        )}`,
      providesTags: ["FollowUpRecordings"]
    }),

    /**
     * Returns 202 + task_id, and creates the generation row synchronously, so
     * invalidating immediately is intended: the refetch picks up
     * `generation_state: "processing"`, which is what starts the poll. A repeat
     * request while one is in flight returns the SAME task_id, so a
     * double-click is harmless.
     *
     * `suppressGlobalErrorToast` covers the network-error and timeout paths
     * only — that is the sole branch in `baseQueryWithReauth` that raises a
     * toast of its own, so the 402 and 409 were never at risk of doubling. The
     * generation hook renders its own copy for every failure including a
     * dropped connection, so without this the user would see both there.
     *
     * Not retried: the repo's `retry()` wrapper is GET-only, which is correct
     * for a billable model call.
     */
    suggestFollowUps: builder.mutation<SuggestFollowUpsResponse, number>({
      query: (ambientScribeId) => ({
        url: `/ai/ambient-scribes/${ambientScribeId}/suggest-follow-ups/`,
        method: "POST"
      }),
      extraOptions: { suppressGlobalErrorToast: true },
      invalidatesTags: ["FollowUpRecordings"]
    }),

    /**
     * Per-task poll. NOT used by the list view, which polls the recordings list
     * instead so that N concurrent generations cost one request and progress
     * survives a page reload. Kept as the documented fallback if the list
     * payload ever proves too heavy to poll.
     *
     * `keepUnusedDataFor: 0` is load-bearing if it is ever adopted: this is a
     * live "state of THIS task" read, not cacheable data, and a retained entry
     * for a completed task_id would be served synchronously as `success` with
     * the old result, ending the poll on stale content.
     */
    getFollowUpGenerationStatus: builder.query<
      FollowUpGenerationTaskStatus,
      string
    >({
      query: (taskId) => `/ai/follow-up-generations/status/${taskId}/`,
      keepUnusedDataFor: 0
    }),

    /* ---- internal / provenance ---- */

    /** `ambient_scribe` is REQUIRED — omitting it is a 400, not an empty list. */
    getFollowUpGenerations: builder.query<
      FollowUpGenerationsResponse,
      FollowUpGenerationsQueryArgs
    >({
      query: (params) =>
        `/ai/follow-up-generations/${formatValidQueryParamUrlCommaSeparated(
          params,
          true
        )}`,
      providesTags: ["FollowUpGenerations"]
    }),

    /** The only route that returns `prompt`. Many KB — fetch on disclosure open. */
    getFollowUpGeneration: builder.query<FollowUpGenerationDetail, number>({
      query: (id) => `/ai/follow-up-generations/${id}/`,
      providesTags: ["FollowUpGenerations"]
    }),

    getFollowUpPromptVersions: builder.query<
      FollowUpPromptVersionsResponse,
      void
    >({
      query: () => "/ai/follow-up-prompt-versions/",
      providesTags: ["FollowUpGenerations"]
    }),

    /**
     * No tags: a transcript is immutable for a recording, so the default cache
     * lifetime is right and nothing should invalidate it. `line` is absolute,
     * so an offset window is a window over the numbering, never a renumbering.
     */
    getTranscriptLines: builder.query<
      TranscriptLinesResponse,
      TranscriptLinesQueryArgs
    >({
      query: ({ ambientScribeId, limit = 500, offset = 0 }) =>
        `/ai/ambient-scribes/${ambientScribeId}/transcript-lines/?limit=${limit}&offset=${offset}`
    })
  })
});

export const {
  useGetFollowUpRecordingsQuery,
  useGetFollowUpCountsQuery,
  useGetFollowUpQuery,
  useUpdateFollowUpStatusMutation,
  useBulkUpdateFollowUpStatusMutation,
  useGetFollowUpKindsQuery,
  useSuggestFollowUpsMutation,
  useGetFollowUpGenerationStatusQuery,
  useGetFollowUpGenerationsQuery,
  useGetFollowUpGenerationQuery,
  useGetFollowUpPromptVersionsQuery,
  useGetTranscriptLinesQuery,
  useLazyGetTranscriptLinesQuery
} = followUpsApi;
